import {
  safeGetItem,
  safeRemoveItem,
  safeSetItem,
} from "Utils/safeLocalStorage";
import { parseISO } from "date-fns";
import api from "./api";

export const LOCALSTORAGE_DONATED_KEY = "memorialPageDonation";

export function updateCommentInList(comment, list) {
  return list.map((listItem) =>
    listItem.id === comment.id ? comment : listItem
  );
}

export function addDonationToLocalStorage(donation) {
  safeSetItem(LOCALSTORAGE_DONATED_KEY, JSON.stringify(donation));
}

export function removeDonationFromLocalStorage() {
  safeRemoveItem(LOCALSTORAGE_DONATED_KEY);
}

export function getDonationFromLocalStorage() {
  const item = safeGetItem(LOCALSTORAGE_DONATED_KEY);

  if (!item) {
    return null;
  }

  try {
    return JSON.parse(item);
  } catch (e) {
    console.error("Error parsing donation data from localStorage", e);

    return null;
  }
}

export function donationGTMTrigger(donation) {
  const data = {
    donation: donation.amount,
    event: "donation",
  };

  window.dataLayer.push(data);
}

export function deleteCommentInList(commentId, list) {
  list.forEach((item, index) => {
    if (item.id === commentId) {
      list.splice(index, 1);
    }
  });

  return list;
}

export function triggerFirstCommentNotice(list, comment) {
  if (!comment || list.length > 2) {
    return null;
  }

  const STORAGE_KEY = "firstCommentNotice";

  if (safeGetItem(STORAGE_KEY)) {
    return null;
  }

  const data = {
    commentId: comment.id,
  };

  return new Promise((resolve, reject) => {
    api
      .post("first-comment-notification", data)
      .then(() => {
        safeSetItem(STORAGE_KEY, "true");
        resolve();
      })
      .catch((error) => reject(error));
  });
}

export function triggerFirstImageNotice(imageList, comment) {
  if (!comment || imageList.length > 1) {
    return null;
  }

  const STORAGE_KEY = "firstImageNotice";

  if (safeGetItem(STORAGE_KEY)) {
    return null;
  }

  const data = {
    commentId: comment.id,
  };

  return new Promise((resolve, reject) => {
    api
      .post("first-image-notification", data)
      .then(() => {
        safeSetItem(STORAGE_KEY, "true");
        resolve();
      })
      .catch((error) => reject(error));
  });
}

export function sortCommentsList(commentsList, key) {
  const storedListRaw = safeGetItem(key);
  const storedList = storedListRaw ? JSON.parse(storedListRaw) : null;

  if (!storedList) {
    return commentsList;
  }

  const firstItemIds = storedList.map(({ id }) => id);

  firstItemIds.forEach((id, index) => {
    const matchedIndex = findMatchedIndexFromId(commentsList, id);

    if (matchedIndex) {
      const indexedItem = commentsList.splice(matchedIndex, 1);
      commentsList.splice(index, 0, indexedItem[0]);
    }
  });

  return commentsList;
}

export function findMatchedIndexFromId(array, filterId) {
  let matchedIndex = null;

  array.some(({ id }, i) => {
    if (id === filterId) {
      matchedIndex = i;
      return true;
    }

    return false;
  });

  return matchedIndex;
}

export const COMMENT_SORT_RELEVANCE = "relevance";
export const COMMENT_SORT_DATE_NEWEST = "date-newest";
export const COMMENT_SORT_DATE_OLDEST = "date-oldest";
export const COMMENT_SORT_NAME_ASC = "name-asc";
export const COMMENT_SORT_NAME_DESC = "name-desc";

const HTML_TAG_REGEX = /<[^>]*>/g;
const WHITESPACE_REGEX = /\s+/g;

function getCommentTime(comment) {
  if (typeof comment?.date !== "string") {
    return null;
  }

  const time = parseISO(comment.date).getTime();

  return Number.isNaN(time) ? null : time;
}

function compareCommentTimes(a, b, newestFirst) {
  const timeA = getCommentTime(a);
  const timeB = getCommentTime(b);

  // Comments with a missing or unparseable date sink to the bottom in both
  // date orders.
  if (timeA === null || timeB === null) {
    return Number(timeA === null) - Number(timeB === null);
  }

  return newestFirst ? timeB - timeA : timeA - timeB;
}

function getCommentFirstName(comment) {
  const name = typeof comment?.name === "string" ? comment.name : "";

  return name
    .replace(HTML_TAG_REGEX, " ")
    .replace(WHITESPACE_REGEX, " ")
    .trim()
    .split(" ")[0]
    .toLowerCase();
}

/**
 * Return a sorted copy of the comments list for the given sort key. The
 * relevance option preserves the order produced by the server scoring (and
 * the created-comment pinning from `sortCommentsList`). Name sorting
 * compares the first name using the given locale's collation so letters
 * like å/ä/ö (sv) or æ/ø/å (da) order correctly.
 *
 * @param {Array<object>} comments The active comments list
 * @param {string} sortBy One of the COMMENT_SORT_* keys
 * @param {string} locale BCP 47 tag used for name collation
 * @returns {Array<object>} A new, sorted list
 */
export function sortComments(comments, sortBy, locale = "sv") {
  if (sortBy === COMMENT_SORT_DATE_NEWEST) {
    return [...comments].sort((a, b) => compareCommentTimes(a, b, true));
  }

  if (sortBy === COMMENT_SORT_DATE_OLDEST) {
    return [...comments].sort((a, b) => compareCommentTimes(a, b, false));
  }

  if (sortBy === COMMENT_SORT_NAME_ASC) {
    return [...comments].sort((a, b) =>
      getCommentFirstName(a).localeCompare(getCommentFirstName(b), locale)
    );
  }

  if (sortBy === COMMENT_SORT_NAME_DESC) {
    return [...comments].sort((a, b) =>
      getCommentFirstName(b).localeCompare(getCommentFirstName(a), locale)
    );
  }

  return [...comments];
}

// no support for unicode, problem? Should we only look for '@'?
const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const PHONE_NUMBER_REGEX = /^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s/0-9]{8,14}$/;

export function validateEmail(email) {
  return EMAIL_REGEX.test(String(email).toLowerCase());
}

export function validatePhoneNumber(phoneNumber) {
  return PHONE_NUMBER_REGEX.test(String(phoneNumber));
}
